import { useEffect, useMemo, useState } from 'react'
import { getVisibleQuestionKeys } from '@artem-lex/common-server/questionnaire/conditional'
import { Loader2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import {
  useGetTypeBQuestionnaire,
  type TypeBQuestionnaireExtractionEvidenceEntry,
} from '@/shared/hooks/api/machineryDirectiveCompliance'
import { Button } from '@/components/ui/button'
import { TypeBQuestionnaireSectionCard } from '../../components/TypeBQuestionnaireStep'
import {
  buildDefaultSectionOpenByKey,
  countIncompleteVisibleQuestions,
  getActiveTypeBQuestionnaireSections,
  isQuestionnaireComplete,
  normalizeTypeBQuestionnaireAnswers,
} from '../../utils/typeBQuestionnaireWizard'

type RerunTypeBQuestionnairePanelProps = {
  complianceId: string
  answersJson: Record<string, unknown>
  evidenceJson: Record<string, unknown>
  extractionSkipped?: boolean
  onAnswersChange: (answers: Record<string, unknown>) => void
  onGenerate: (answers: Record<string, unknown>) => void
  isGenerating: boolean
}

function normalizeExtractionEvidence(
  raw: Record<string, unknown>
): Record<string, TypeBQuestionnaireExtractionEvidenceEntry | null> {
  const normalized: Record<
    string,
    TypeBQuestionnaireExtractionEvidenceEntry | null
  > = {}
  for (const [questionKey, value] of Object.entries(raw)) {
    if (value == null) {
      normalized[questionKey] = null
      continue
    }
    if (typeof value !== 'object') {
      continue
    }
    const entry = value as Record<string, unknown>
    const rationale =
      typeof entry.rationale === 'string' ? entry.rationale.trim() : ''
    if (rationale.length === 0) {
      normalized[questionKey] = null
      continue
    }
    normalized[questionKey] = {
      rationale,
      sourceFilename:
        typeof entry.sourceFilename === 'string' ? entry.sourceFilename : null,
      excerpt: typeof entry.excerpt === 'string' ? entry.excerpt : null,
    }
  }
  return normalized
}

function hasAnyFilledAnswer(
  answers: Record<string, boolean | string | null>
): boolean {
  return Object.values(answers).some((value) => {
    if (typeof value === 'boolean') {
      return true
    }
    if (typeof value === 'string') {
      return value.trim().length > 0
    }
    return false
  })
}

export function RerunTypeBQuestionnairePanel({
  complianceId,
  answersJson,
  evidenceJson,
  extractionSkipped = false,
  onAnswersChange,
  onGenerate,
  isGenerating,
}: RerunTypeBQuestionnairePanelProps) {
  const { t } = useTranslation('machineryDirectiveCompliance')
  const questionnaireQuery = useGetTypeBQuestionnaire(complianceId)
  const definition = questionnaireQuery.data?.definition

  const answers = useMemo(() => {
    if (definition == null) {
      return {}
    }
    return normalizeTypeBQuestionnaireAnswers(definition, answersJson)
  }, [answersJson, definition])

  const extractionEvidence = useMemo(
    () => normalizeExtractionEvidence(evidenceJson),
    [evidenceJson]
  )

  const [textDraftByQuestionKey, setTextDraftByQuestionKey] = useState<
    Record<string, string>
  >({})
  const [sectionOpenByKey, setSectionOpenByKey] = useState<
    Record<string, boolean>
  >({})
  const [dismissedInsufficientHintKeys, setDismissedInsufficientHintKeys] =
    useState<Set<string>>(() => new Set())
  const [showValidationErrors, setShowValidationErrors] = useState(false)
  const [hasSeededSectionOpen, setHasSeededSectionOpen] = useState(false)

  useEffect(() => {
    if (definition == null) {
      return
    }
    setTextDraftByQuestionKey((previous) => {
      let hasChanges = false
      const next = { ...previous }
      for (const question of definition.questions) {
        if (question.kind !== 'text') {
          continue
        }
        const persistedValue =
          typeof answers[question.questionKey] === 'string'
            ? String(answers[question.questionKey])
            : ''
        if (
          !(question.questionKey in next) ||
          next[question.questionKey] !== persistedValue
        ) {
          next[question.questionKey] = persistedValue
          hasChanges = true
        }
      }
      if (!hasChanges) {
        return previous
      }
      return next
    })
  }, [answers, definition])

  const visibleKeySet = useMemo(() => {
    if (definition == null) {
      return new Set<string>()
    }
    return new Set(getVisibleQuestionKeys(definition, answers))
  }, [answers, definition])

  const activeSections = useMemo(() => {
    if (definition == null) {
      return []
    }
    return getActiveTypeBQuestionnaireSections({
      definition,
      visibleQuestionKeys: visibleKeySet,
    })
  }, [definition, visibleKeySet])

  useEffect(() => {
    if (
      definition == null ||
      activeSections.length === 0 ||
      hasSeededSectionOpen
    ) {
      return
    }
    setSectionOpenByKey(
      buildDefaultSectionOpenByKey({
        activeSections,
        definition,
        visibleQuestionKeys: visibleKeySet,
        answers,
        textDraftByQuestionKey,
      })
    )
    setHasSeededSectionOpen(true)
  }, [
    activeSections,
    answers,
    definition,
    hasSeededSectionOpen,
    textDraftByQuestionKey,
    visibleKeySet,
  ])

  const remainingQuestionCount = useMemo(() => {
    if (definition == null) {
      return 0
    }
    return countIncompleteVisibleQuestions({
      activeSections,
      definition,
      visibleQuestionKeys: visibleKeySet,
      answers,
      textDraftByQuestionKey,
    })
  }, [
    activeSections,
    answers,
    definition,
    textDraftByQuestionKey,
    visibleKeySet,
  ])

  const isComplete =
    definition != null &&
    isQuestionnaireComplete({
      wizardSections: activeSections,
      definition,
      visibleQuestionKeys: visibleKeySet,
      answers,
      textDraftByQuestionKey,
    })

  const showNoExtractionBanner =
    extractionSkipped || !hasAnyFilledAnswer(answers)

  const dismissInsufficientEvidenceHint = (questionKey: string) => {
    setDismissedInsufficientHintKeys((previous) => {
      if (previous.has(questionKey)) {
        return previous
      }
      const next = new Set(previous)
      next.add(questionKey)
      return next
    })
  }

  const handleAnswer = (questionKey: string, value: unknown) => {
    dismissInsufficientEvidenceHint(questionKey)
    let nextValue: boolean | string | null = null
    if (typeof value === 'boolean' || typeof value === 'string') {
      nextValue = value
    }
    onAnswersChange({ ...answersJson, [questionKey]: nextValue })
  }

  const handleTextDraftChange = (questionKey: string, value: string) => {
    dismissInsufficientEvidenceHint(questionKey)
    setTextDraftByQuestionKey((previous) => ({
      ...previous,
      [questionKey]: value,
    }))
  }

  const handleTextAnswerBlur = (questionKey: string) => {
    if (definition == null) {
      return
    }
    const question = definition.questions.find(
      (entry) => entry.questionKey === questionKey
    )
    if (question == null || question.kind !== 'text') {
      return
    }
    const draft = textDraftByQuestionKey[questionKey] ?? ''
    const persisted =
      typeof answers[questionKey] === 'string' ? String(answers[questionKey]) : ''
    if (draft === persisted) {
      return
    }
    onAnswersChange({ ...answersJson, [questionKey]: draft })
  }

  const buildFlushedAnswers = (): Record<string, unknown> => {
    const flushed = { ...answersJson }
    if (definition == null) {
      return flushed
    }
    for (const question of definition.questions) {
      if (question.kind !== 'text') {
        continue
      }
      if (question.questionKey in textDraftByQuestionKey) {
        flushed[question.questionKey] =
          textDraftByQuestionKey[question.questionKey]
      }
    }
    return flushed
  }

  const handleGenerate = () => {
    if (!isComplete) {
      setShowValidationErrors(true)
      return
    }
    const flushedAnswers = buildFlushedAnswers()
    onAnswersChange(flushedAnswers)
    onGenerate(flushedAnswers)
  }

  if (questionnaireQuery.isLoading || definition == null) {
    return (
      <div className='flex flex-1 items-center justify-center'>
        <Loader2 className='size-8 animate-spin text-muted-foreground' />
      </div>
    )
  }

  if (questionnaireQuery.isError) {
    return (
      <div className='space-y-4'>
        <p className='text-sm text-muted-foreground'>
          {t('onboarding.typeBQuestionnaire.loadFailed')}
        </p>
        <div className='flex justify-end'>
          <Button
            type='button'
            onClick={() => {
              void questionnaireQuery.refetch()
            }}
            disabled={questionnaireQuery.isFetching}
          >
            {questionnaireQuery.isFetching && (
              <Loader2 className='mr-2 size-4 animate-spin' />
            )}
            {t('onboarding.typeBQuestionnaire.retryLoadButton')}
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div className='flex min-h-0 flex-1 flex-col gap-4'>
      <div className='space-y-2'>
        <h2 className='text-lg font-semibold tracking-tight'>
          {t('onboarding.typeBQuestionnaire.title')}
        </h2>
        <p className='text-sm leading-relaxed text-muted-foreground'>
          {t('onboarding.typeBQuestionnaire.intro')}
        </p>
      </div>

      {showNoExtractionBanner && (
        <p className='text-sm text-muted-foreground'>
          {extractionSkipped
            ? t('standardsRerun.typeBQuestionnaire.noSources')
            : t('standardsRerun.typeBQuestionnaire.noExtractionAnswers')}
        </p>
      )}

      <div className='flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto pr-1'>
        {activeSections.map((section) => (
          <TypeBQuestionnaireSectionCard
            key={section.sectionKey}
            section={section}
            definition={definition}
            visibleKeySet={visibleKeySet}
            effectiveAnswers={answers}
            textDraftByQuestionKey={textDraftByQuestionKey}
            extractionEvidence={extractionEvidence}
            dismissedInsufficientHintKeys={dismissedInsufficientHintKeys}
            isOpen={sectionOpenByKey[section.sectionKey]}
            showValidationErrors={showValidationErrors}
            isQuestionnaireBusy={isGenerating}
            isTextInputDisabled={isGenerating}
            onOpenChange={(open) => {
              setSectionOpenByKey((previous) => ({
                ...previous,
                [section.sectionKey]: open,
              }))
            }}
            onAnswer={handleAnswer}
            onTextDraftChange={handleTextDraftChange}
            onTextAnswerBlur={handleTextAnswerBlur}
            translate={t}
          />
        ))}
      </div>

      <div className='shrink-0 border-t pt-3'>
        <div className='flex flex-wrap items-center justify-between gap-3'>
          <div className='space-y-1'>
            {remainingQuestionCount > 0 ? (
              <p className='text-sm text-muted-foreground'>
                {t('onboarding.typeBQuestionnaire.questionsRemaining', {
                  count: remainingQuestionCount,
                })}
              </p>
            ) : (
              <p className='text-sm text-muted-foreground'>
                {t('onboarding.typeBQuestionnaire.readyToGenerate')}
              </p>
            )}
            {showValidationErrors && remainingQuestionCount > 0 ? (
              <p className='text-sm font-medium text-destructive'>
                {t('onboarding.typeBQuestionnaire.completeQuestionnaireHint')}
              </p>
            ) : null}
          </div>
          <Button
            type='button'
            onClick={handleGenerate}
            disabled={isGenerating}
            title={
              !isComplete
                ? t('onboarding.typeBQuestionnaire.generateDisabledTooltip')
                : undefined
            }
          >
            {isGenerating && (
              <Loader2 className='mr-2 size-4 animate-spin' />
            )}
            {t('standardsRerun.typeBQuestionnaire.generateButton')}
          </Button>
        </div>
      </div>
    </div>
  )
}
